import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveHeader } from '../siteHeader';
import { fetchSite, getToken, patchHeader, uploadFile } from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

const cardClass = 'rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5';
const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90 disabled:opacity-50';
const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';
const ghostBtnClass =
  'rounded-lg px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100';

function ModalFooter({ onCancel, onApply, applyLabel = 'Apply' }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} className={primaryBtnClass}>
        {applyLabel}
      </button>
    </div>
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

function newNavLinkId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `nl-${crypto.randomUUID()}`;
  }
  return `nl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function ensureNavLinkIds(navLinks) {
  return (Array.isArray(navLinks) ? navLinks : []).map((row) => ({
    ...row,
    _id: typeof row?._id === 'string' && row._id.trim() ? row._id.trim() : newNavLinkId(),
  }));
}

function ensureHeaderFormShape(siteLike) {
  const base = resolveHeader(siteLike);
  return { ...base, navLinks: ensureNavLinkIds(base.navLinks) };
}

function emptyNavLink() {
  return {
    _id: newNavLinkId(),
    label: '',
    href: '#',
    current: false,
    dropdown: false,
    dropdownSource: '',
    dropdownItems: [],
  };
}

/** Universal drag handle (2×3 dots). */
function DragHandleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <circle cx="6" cy="5" r="1.75" />
      <circle cx="14" cy="5" r="1.75" />
      <circle cx="6" cy="10" r="1.75" />
      <circle cx="14" cy="10" r="1.75" />
      <circle cx="6" cy="15" r="1.75" />
      <circle cx="14" cy="15" r="1.75" />
    </svg>
  );
}

function NavLinkDragPreview({ row }) {
  const labelText = row?.label?.trim() || 'Navigation link';
  const hrefText = row?.href?.trim() || '—';
  return (
    <div className="w-full max-w-2xl cursor-grabbing rounded-xl border-2 border-brand-olive/50 bg-white p-4 shadow-2xl ring-4 ring-brand-olive/15">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-olive">
        <DragHandleIcon className="h-4 w-4 shrink-0 text-brand-forest" />
        Reorder preview
      </p>
      <p className="mt-2 text-base font-bold text-brand-forest">{labelText}</p>
      <p className="mt-1 truncate font-mono text-sm text-neutral-600">{hrefText}</p>
      {row?.dropdown ? (
        <p className="mt-2 inline-block rounded-full bg-brand-lime-soft/80 px-2 py-0.5 text-xs font-semibold text-brand-forest">
          Dropdown
          {row?.dropdownSource === 'seedCategories' ? ' · categories' : ''}
          {row?.dropdownSource === 'custom' ? ` · custom (${(row?.dropdownItems || []).filter((c) => c?.label?.trim() && c?.href?.trim()).length})` : ''}
        </p>
      ) : null}
    </div>
  );
}

function SortableNavLinkRow({ id, index, row, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dd =
    row.dropdown && row.dropdownSource === 'seedCategories'
      ? 'Categories'
      : row.dropdown && row.dropdownSource === 'custom'
        ? `Custom (${(row.dropdownItems || []).filter((c) => c?.label?.trim() && c?.href?.trim()).length})`
        : row.dropdown
          ? 'Chevron'
          : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex flex-wrap items-stretch gap-2 rounded-lg border p-2 sm:items-center sm:gap-3 sm:p-3 ${
        isDragging
          ? 'border-dashed border-brand-olive/50 bg-brand-cream/50 opacity-55 shadow-inner'
          : 'border-neutral-200 bg-white shadow-sm'
      }`}
    >
      <div className="flex shrink-0 items-center justify-center self-center">
        <button
          type="button"
          title="Drag to reorder"
          aria-label={`Drag to reorder: ${row.label?.trim() || `Link ${index + 1}`}`}
          className="inline-flex cursor-grab touch-none items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 p-2 text-neutral-500 shadow-sm hover:border-brand-olive/40 hover:bg-brand-lime-soft/30 hover:text-brand-forest active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <DragHandleIcon className="h-5 w-5 shrink-0 select-none" />
        </button>
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        <p className="m-0 text-sm font-semibold text-neutral-900">{clip(row.label, 42)}</p>
        <p className="mt-0.5 truncate font-mono text-xs text-neutral-600">{clip(row.href, 56)}</p>
        <p className="mt-1 text-[11px] text-neutral-500">
          {row.current ? <span className="mr-2 font-medium text-brand-forest">Current</span> : null}
          {dd ? <span>{dd}</span> : <span>Single link</span>}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1 self-center">
        <button type="button" className={ghostBtnClass} onClick={() => onEdit(index)}>
          Edit
        </button>
        <button type="button" className={`${ghostBtnClass} text-red-700`} onClick={() => onRemove(index)}>
          Remove
        </button>
      </div>
    </div>
  );
}

export default function HeaderDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ensureHeaderFormShape(DEFAULT_SITE));
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeNavId, setActiveNavId] = useState(null);

  const [modalErr, setModalErr] = useState('');
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [brandDraft, setBrandDraft] = useState(null);
  const [navModalOpen, setNavModalOpen] = useState(false);
  const [navModalIndex, setNavModalIndex] = useState(null);
  const [navDraft, setNavDraft] = useState(null);
  const [childModalOpen, setChildModalOpen] = useState(false);
  const [childEditIndex, setChildEditIndex] = useState(null);
  const [childDraft, setChildDraft] = useState({ label: '', href: '' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const navLinkIds = (form.navLinks || []).map((r) => r._id);
  const activeRow = activeNavId ? (form.navLinks || []).find((r) => r._id === activeNavId) : null;

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => setForm(ensureHeaderFormShape(site)))
      .catch(() => setForm(ensureHeaderFormShape(DEFAULT_SITE)));
  }, [navigate]);

  function openBrandModal() {
    setModalErr('');
    setBrandDraft({
      logoUrl: form.logoUrl || '',
      logoAlt: form.logoAlt || '',
      brandPrimary: form.brandPrimary || '',
      brandSecondary: form.brandSecondary || '',
      brandHref: form.brandHref || '',
    });
    setBrandModalOpen(true);
  }

  function closeBrandModal() {
    setBrandModalOpen(false);
    setBrandDraft(null);
    setModalErr('');
  }

  function applyBrandModal() {
    if (!brandDraft) return;
    setForm((f) => ({
      ...f,
      logoUrl: brandDraft.logoUrl || '',
      logoAlt: brandDraft.logoAlt || '',
      brandPrimary: brandDraft.brandPrimary || '',
      brandSecondary: brandDraft.brandSecondary || '',
      brandHref: brandDraft.brandHref || '',
    }));
    closeBrandModal();
  }

  function openNavModal(index) {
    setModalErr('');
    const links = form.navLinks || [];
    if (index == null && links.length >= 10) {
      setErr('Maximum 10 navigation links.');
      return;
    }
    if (index == null) {
      setNavDraft(emptyNavLink());
      setNavModalIndex(null);
    } else {
      const row = links[index];
      if (!row) return;
      setNavDraft({
        ...row,
        dropdownItems: Array.isArray(row.dropdownItems)
          ? row.dropdownItems.map((c) => ({ label: c?.label || '', href: c?.href || '' }))
          : [],
      });
      setNavModalIndex(index);
    }
    setNavModalOpen(true);
  }

  function closeNavModal() {
    setNavModalOpen(false);
    setNavModalIndex(null);
    setNavDraft(null);
    setModalErr('');
    setChildModalOpen(false);
    setChildEditIndex(null);
    setChildDraft({ label: '', href: '' });
  }

  function applyNavModal() {
    if (!navDraft) return;
    const label = String(navDraft.label || '').trim();
    const href = String(navDraft.href || '').trim();
    if (!label || !href) {
      setModalErr('Label and href are required.');
      return;
    }
    let dropdown = Boolean(navDraft.dropdown);
    const src = dropdown ? String(navDraft.dropdownSource || '').trim() : '';
    let dropdownSource = '';
    let dropdownItems = [];
    if (dropdown) {
      if (src === 'seedCategories') dropdownSource = 'seedCategories';
      else if (src === 'custom') {
        dropdownSource = 'custom';
        dropdownItems = (Array.isArray(navDraft.dropdownItems) ? navDraft.dropdownItems : [])
          .map((ch) => ({
            label: String(ch?.label || '').trim(),
            href: String(ch?.href || '').trim(),
          }))
          .filter((ch) => ch.label && ch.href)
          .slice(0, 20);
      }
    } else {
      dropdown = false;
    }

    const existingId =
      navModalIndex != null && form.navLinks[navModalIndex] ? form.navLinks[navModalIndex]._id : newNavLinkId();
    const base = {
      _id: existingId,
      label,
      href,
      current: Boolean(navDraft.current),
      dropdown,
      dropdownSource,
    };
    const nextRow = dropdownSource === 'custom' ? { ...base, dropdownItems } : { ...base, dropdownItems: [] };

    setForm((f) => {
      const list = f.navLinks || [];
      if (navModalIndex == null) {
        return { ...f, navLinks: [...list, nextRow].slice(0, 10) };
      }
      return {
        ...f,
        navLinks: list.map((r, i) => (i === navModalIndex ? { ...nextRow, _id: r._id } : r)),
      };
    });
    closeNavModal();
  }

  function openChildModal(editIndex) {
    if (!navDraft) return;
    setModalErr('');
    setChildEditIndex(editIndex);
    if (editIndex == null) {
      setChildDraft({ label: '', href: '' });
    } else {
      const c = (navDraft.dropdownItems || [])[editIndex] || {};
      setChildDraft({ label: c.label || '', href: c.href || '' });
    }
    setChildModalOpen(true);
  }

  function closeChildModal() {
    setChildModalOpen(false);
    setChildEditIndex(null);
    setChildDraft({ label: '', href: '' });
    setModalErr('');
  }

  function applyChildModal() {
    const label = String(childDraft.label || '').trim();
    const href = String(childDraft.href || '').trim();
    if (!label || !href) {
      setModalErr('Label and href are required for each child link.');
      return;
    }
    setNavDraft((d) => {
      if (!d) return d;
      const items = [...(d.dropdownItems || [])];
      if (childEditIndex == null) {
        if (items.length >= 20) return d;
        items.push({ label, href });
      } else {
        items[childEditIndex] = { label, href };
      }
      return { ...d, dropdownItems: items };
    });
    closeChildModal();
  }

  function removeNavChild(ci) {
    setNavDraft((d) => {
      if (!d) return d;
      return { ...d, dropdownItems: (d.dropdownItems || []).filter((_, j) => j !== ci) };
    });
  }

  function removeLink(i) {
    if (!window.confirm('Remove this navigation link?')) return;
    setForm((f) => ({
      ...f,
      navLinks: f.navLinks.filter((_, idx) => idx !== i),
    }));
  }

  function handleNavDragStart(event) {
    setActiveNavId(String(event.active.id));
  }

  function handleNavDragEnd(event) {
    setActiveNavId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const links = f.navLinks || [];
      const oldIndex = links.findIndex((l) => l._id === active.id);
      const newIndex = links.findIndex((l) => l._id === over.id);
      if (oldIndex < 0 || newIndex < 0) return f;
      return { ...f, navLinks: arrayMove(links, oldIndex, newIndex) };
    });
  }

  function handleNavDragCancel() {
    setActiveNavId(null);
  }

  async function onBrandLogoUpload(file) {
    if (!file) return;
    setErr('');
    setUploadingLogo(true);
    try {
      const { url } = await uploadFile(file);
      setBrandDraft((d) => (d ? { ...d, logoUrl: url } : d));
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setUploadingLogo(false);
    }
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const payload = {
        logoUrl: form.logoUrl,
        logoAlt: form.logoAlt,
        brandPrimary: form.brandPrimary,
        brandSecondary: form.brandSecondary,
        brandHref: form.brandHref,
        navLinks: (Array.isArray(form.navLinks) ? form.navLinks : [])
          .map((row) => {
            const dropdown = Boolean(row?.dropdown);
            const src = dropdown ? String(row?.dropdownSource || '').trim() : '';
            let dropdownSource = '';
            let dropdownItems = [];
            if (dropdown) {
              if (src === 'seedCategories') dropdownSource = 'seedCategories';
              else if (src === 'custom') {
                dropdownSource = 'custom';
                dropdownItems = (Array.isArray(row.dropdownItems) ? row.dropdownItems : [])
                  .map((ch) => ({
                    label: String(ch?.label || '').trim(),
                    href: String(ch?.href || '').trim(),
                  }))
                  .filter((ch) => ch.label && ch.href)
                  .slice(0, 20);
              }
            }
            const base = {
              label: String(row?.label || '').trim(),
              href: String(row?.href || '').trim(),
              current: Boolean(row?.current),
              dropdown,
              dropdownSource,
            };
            return dropdownSource === 'custom' ? { ...base, dropdownItems } : base;
          })
          .filter((row) => row.label && row.href),
      };
      const { header } = await patchHeader(payload);
      setForm(ensureHeaderFormShape({ header: header || payload }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-neutral-900">Header</h1>
          <p className="text-sm text-neutral-600">Logo, brand text, and navigation links</p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-lg bg-brand-olive px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="grid w-full gap-6">
          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Brand & logo</h2>
                <p className="mt-1 text-sm text-neutral-600">
                  {clip(form.brandPrimary, 24)} · {clip(form.brandSecondary, 24)}
                </p>
              </div>
              <button type="button" onClick={openBrandModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
            {form.logoUrl ? (
              <div className="mt-4 flex max-w-xs items-center gap-3">
                <img
                  src={form.logoUrl}
                  alt={form.logoAlt || ''}
                  className="h-16 w-16 shrink-0 rounded-full object-cover ring-1 ring-neutral-200"
                />
                <p className="m-0 text-xs text-neutral-500">Logo set · edit in dialog to replace.</p>
              </div>
            ) : (
              <p className="mt-3 text-sm text-neutral-500">No logo yet. Open the dialog to upload or paste a URL.</p>
            )}
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="m-0 text-base font-bold text-neutral-900">Navigation links</h2>
                <p className="mt-1 text-xs text-neutral-500">
                  Drag the handle to reorder. Use Edit to change a link or its dropdown. Save to publish.
                </p>
              </div>
              <button type="button" onClick={() => openNavModal(null)} className={secondaryBtnClass}>
                Add link
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleNavDragStart}
              onDragEnd={handleNavDragEnd}
              onDragCancel={handleNavDragCancel}
            >
              <SortableContext items={navLinkIds} strategy={verticalListSortingStrategy}>
                <div className="mt-4 space-y-2">
                  {(form.navLinks || []).map((row, i) => (
                    <SortableNavLinkRow
                      key={row._id}
                      id={row._id}
                      index={i}
                      row={row}
                      onEdit={openNavModal}
                      onRemove={removeLink}
                    />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay
                dropAnimation={{
                  duration: 220,
                  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
                }}
                style={{ cursor: 'grabbing' }}
              >
                {activeRow ? <NavLinkDragPreview row={activeRow} /> : null}
              </DragOverlay>
            </DndContext>
          </section>
        </div>
      </div>

      <AdminModal
        open={brandModalOpen}
        title="Brand & logo"
        description="Brand text, home link for the logo block, and circular logo image."
        error={modalErr}
        onClose={closeBrandModal}
        footer={
          <ModalFooter onCancel={closeBrandModal} onApply={applyBrandModal} />
        }
      >
        {brandDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Brand primary text
              <input
                className={input}
                value={brandDraft.brandPrimary}
                onChange={(e) => setBrandDraft((d) => ({ ...d, brandPrimary: e.target.value }))}
              />
            </label>
            <label className={label}>
              Brand secondary text
              <input
                className={input}
                value={brandDraft.brandSecondary}
                onChange={(e) => setBrandDraft((d) => ({ ...d, brandSecondary: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Brand link href
              <input
                className={input}
                value={brandDraft.brandHref}
                onChange={(e) => setBrandDraft((d) => ({ ...d, brandHref: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Logo alt text
              <input
                className={input}
                value={brandDraft.logoAlt}
                onChange={(e) => setBrandDraft((d) => ({ ...d, logoAlt: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Logo image URL
              <input
                className={input}
                value={brandDraft.logoUrl}
                onChange={(e) => setBrandDraft((d) => ({ ...d, logoUrl: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Upload logo
              <input
                type="file"
                accept="image/*"
                disabled={uploadingLogo}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onBrandLogoUpload(f);
                }}
              />
            </label>
            {uploadingLogo ? <p className="text-xs text-neutral-500 md:col-span-2">Uploading…</p> : null}
            <div className="md:col-span-2">
              <AdminImagePreview src={brandDraft.logoUrl} alt={brandDraft.logoAlt || ''} />
            </div>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={navModalOpen}
        title={navModalIndex == null ? 'Add navigation link' : 'Edit navigation link'}
        description="Label and href are required. Optional dropdown for mega-menu behavior."
        error={modalErr}
        onClose={closeNavModal}
        footer={<ModalFooter onCancel={closeNavModal} onApply={applyNavModal} />}
      >
        {navDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Label
              <input
                className={input}
                value={navDraft.label}
                onChange={(e) => {
                  setNavDraft((d) => ({ ...d, label: e.target.value }));
                  setModalErr('');
                }}
              />
            </label>
            <label className={label}>
              Href
              <input
                className={input}
                value={navDraft.href}
                onChange={(e) => {
                  setNavDraft((d) => ({ ...d, href: e.target.value }));
                  setModalErr('');
                }}
                placeholder="/about or #section"
              />
            </label>
            <div className="flex flex-wrap gap-4 md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700">
                <input
                  type="checkbox"
                  checked={Boolean(navDraft.current)}
                  onChange={(e) => setNavDraft((d) => ({ ...d, current: e.target.checked }))}
                />
                Mark as current (active) link
              </label>
              <label className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700">
                <input
                  type="checkbox"
                  checked={Boolean(navDraft.dropdown)}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setNavDraft((d) => ({
                      ...d,
                      dropdown: on,
                      dropdownSource: on ? d.dropdownSource || '' : '',
                      dropdownItems: on ? (Array.isArray(d.dropdownItems) ? d.dropdownItems : []) : [],
                    }));
                  }}
                />
                Dropdown
              </label>
            </div>
            {navDraft.dropdown ? (
              <label className={`${label} md:col-span-2`}>
                Dropdown type
                <select
                  className={input}
                  value={
                    navDraft.dropdownSource === 'seedCategories'
                      ? 'seedCategories'
                      : navDraft.dropdownSource === 'custom'
                        ? 'custom'
                        : ''
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setNavDraft((d) => ({
                      ...d,
                      dropdownSource: v === 'seedCategories' ? 'seedCategories' : v === 'custom' ? 'custom' : '',
                      dropdownItems: v === 'custom' ? (Array.isArray(d.dropdownItems) ? d.dropdownItems : []) : [],
                    }));
                  }}
                >
                  <option value="">Chevron only (no submenu links)</option>
                  <option value="seedCategories">Variety categories (from Seed varieties admin)</option>
                  <option value="custom">Custom links</option>
                </select>
              </label>
            ) : null}
            {navDraft.dropdown && navDraft.dropdownSource === 'custom' ? (
              <div className="md:col-span-2">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-neutral-800">Child menu links</span>
                  <button
                    type="button"
                    disabled={(navDraft.dropdownItems || []).length >= 20}
                    onClick={() => openChildModal(null)}
                    className={`${secondaryBtnClass} text-xs`}
                  >
                    Add child link
                  </button>
                </div>
                <div className="space-y-2 rounded-lg border border-neutral-200 bg-neutral-50/80 p-3">
                  {(navDraft.dropdownItems || []).length ? (
                    (navDraft.dropdownItems || []).map((child, ci) => (
                      <div
                        key={`dc-${ci}-${child.href}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-neutral-900">{clip(child.label, 36)}</span>
                          <span className="mt-0.5 block truncate font-mono text-xs text-neutral-600">{child.href}</span>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <button type="button" className={ghostBtnClass} onClick={() => openChildModal(ci)}>
                            Edit
                          </button>
                          <button type="button" className={`${ghostBtnClass} text-red-700`} onClick={() => removeNavChild(ci)}>
                            Remove
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-neutral-500">
                      No child links yet. Add at least one for the custom submenu to appear on the site.
                    </p>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={childModalOpen}
        title={childEditIndex == null ? 'Add child link' : 'Edit child link'}
        description="Both label and href are required."
        error={modalErr}
        onClose={closeChildModal}
        footer={<ModalFooter onCancel={closeChildModal} onApply={applyChildModal} />}
      >
        <div className="grid gap-4">
          <label className={label}>
            Label
            <input
              className={input}
              value={childDraft.label}
              onChange={(e) => {
                setChildDraft((d) => ({ ...d, label: e.target.value }));
                setModalErr('');
              }}
            />
          </label>
          <label className={label}>
            Href
            <input
              className={input}
              value={childDraft.href}
              onChange={(e) => {
                setChildDraft((d) => ({ ...d, href: e.target.value }));
                setModalErr('');
              }}
              placeholder="/path or #section"
            />
          </label>
        </div>
      </AdminModal>
    </>
  );
}
